/* globals VXConfig */

import React, {lazy}                          from 'react';
import PropTypes                              from 'prop-types';
import Flux                                   from '../../flux/Flux';
import Translations                           from '../../utils/Translations';
import {VXPay}                                from '../../utils/VXPay';
import {formatCurrency, gaHelper, gtagHelper} from '../../utils/CommonUtils';
import EnumGA4Events                          from "../../utils/EnumGA4Events";
import {ModalBoxEnum, openModalBox}           from "../ModalBox/ModalBoxFactory.js";
import withSuspense                           from '../HigherOrderComponents/Utility/withSuspense.js';

const SideIncentive                = lazy(() => import('./SideIncentive'));
const BuyAlbumPurchaseBox          = lazy(() => import('../ConversionBox/BuyAlbumPurchaseBox'));
const VideoPreviewEndedAvsBox      = lazy(() => import('../ConversionBox/VideoPreviewEndedAvsBox'));
const VideoPreviewEndedPurchaseBox = lazy(() => import('../ConversionBox/VideoPreviewEndedPurchaseBox'));
const VideoPreviewEndedSaleBox     = lazy(() => import('../ConversionBox/VideoPreviewEndedSaleBox'));
const VideoPreviewEndedSignupBox   = lazy(() => import('../ConversionBox/VideoPreviewEndedSignupBox'));
const VideoPreviewEndedVipBox      = lazy(() => import('../ConversionBox/VideoPreviewEndedVipBox'));
const BonusCounterPopup            = lazy(() => import('../ConversionBox/BonusCounterPopup'));
const BonusCounterPopupClip        = lazy(() => import('../ConversionBox/BonusCounterPopupClip'));
const ExitIntentBox                = lazy(() => import('../ExitIntent/ExitIntentBox'));
const BalanceRefillPopup           = lazy(() => import('../ConversionBox/BalanceRefillPopup'));
const LucyCatVideoBox              = lazy(() => import('../ConversionBox/LucyCatVideoBox'));
const EggSuccessBox                = lazy(() => import('../Easter/EggSuccessBox'));
const EasterEgg                    = lazy(() => import('../Easter/EasterEgg'));

const onBonusCodeRedeemed = (result) => {
	if (result.code?.startsWith('SPECIAL-TOPUP')) {
		openModalBox(ModalBoxEnum.SPECIAL_TOP_UP, {overCookie: true, code: result.code});
	}
};

class Conversion extends React.Component {

	static processTrackingData() {
		const trackingData = Flux.Conversion.getTrackingData();

		if (trackingData['AdWords'] !== undefined) {
			for (const i in trackingData['AdWords']) {
				gtagHelper('event', 'conversion', trackingData['AdWords'][i]);
			}
		}
	}

	constructor(props) {
		super(props);

		this.state = {
			exitIntentIncentive:         null,
			exitIntentExtendedIncentive: null,
			overlayFullSizeIncentive:    null,
			overlayIncentive:            null,
			sideLeftIncentive:           null,
			sideRightIncentive:          null,
			showEasterNotification:      null,
			showExitIntentIncentive:     true,
			showSideLeftIncentive:       true,
			showSideRightIncentive:      true,
            isCountdownFinished:         false,
		};

		this.onCloseExitIncentive            = this.onCloseExitIncentive.bind(this);
		this.onCloseOverlayFullSizeIncentive = this.onCloseOverlayFullSizeIncentive.bind(this);
		this.onCloseOverlayIncentive         = this.onCloseOverlayIncentive.bind(this);
		this.onCloseEasterNotification       = this.onCloseEasterNotification.bind(this);
		this.onIncentivesChange              = this.onIncentivesChange.bind(this);
		this.onMenuChange                    = this.onMenuChange.bind(this);
		this.onEasterNotification            = this.onEasterNotification.bind(this);
		this.toggleBodyScroll                = this.toggleBodyScroll.bind(this);
        this.onCountdownFinished             = this.onCountdownFinished.bind(this);
	}

	componentDidMount() {
		Flux.Conversion.addIncentivesChangeListener(this.onIncentivesChange);
		Flux.Special.addEasterChangeListener(this.onEasterNotification);
		if (this.props.isMobile) {
			Flux.VXMobile.addMenuChangeListener(this.onMenuChange);
		}

		// initial incentive loading
		if (Flux.Guest.isLoggedIn()) {
			Flux.Conversion.loadPageLoadIncentives(VXConfig.routeName, VXConfig.routeArgs);
		} else {
			VXPay.hasLoginCookie(Flux.Conversion.setGuestAlreadySignedUp);
		}

		// register bonus code listener
		if (Flux.Guest.isLoggedIn()) {
			VXPay.onBonusCodeRedeemed(onBonusCodeRedeemed);
		}
	}

	componentWillUnmount() {
		Flux.Conversion.removeIncentivesChangeListener(this.onIncentivesChange);
		Flux.Special.removeEasterChangeListener(this.onEasterNotification);
		if (this.props.isMobile) {
			Flux.VXMobile.removeMenuChangeListener(this.onMenuChange);
		}
	}

	onEasterNotification() {
		this.setState({
			showEasterNotification: true,
		});
	}

	onCloseExitIncentive() {
		// when closing the exit intent incentive fetch the next one from the queue
		this.setState({
			exitIntentIncentive: null,
		});
	}

	onCloseOverlayFullSizeIncentive() {
		if (this.state.overlayFullSizeIncentive) {
			Flux.Conversion.closeIncentive(this.state.overlayFullSizeIncentive);
		}

		this.setState({
			overlayFullSizeIncentive: null,
		}, () => {
			this.toggleBodyScroll('overlayFullSizeIncentive', null);
		});
	}

	onCloseOverlayIncentive() {
		if (this.state.overlayIncentive) {
			Flux.Conversion.closeIncentive(this.state.overlayIncentive);
		}

		this.setState({
			overlayIncentive: null,
		});
	}

	onCloseEasterNotification() {
		this.setState({
			showEasterNotification: null,
		});
	}

	onIncentivesChange(type, region) {
		switch (type) {
			case Flux.Constants.ConversionEventTypes.CLEAR: {
				switch (region) {
					case Flux.Constants.ConversionIncentiveRegions.OVERLAY_FULL_SIZE:
						this.onCloseOverlayFullSizeIncentive();
						break;
					case Flux.Constants.ConversionIncentiveRegions.SIDE_LEFT:
						this.setState({
							sideLeftIncentive: null,
						});
						break;
					case Flux.Constants.ConversionIncentiveRegions.SIDE_RIGHT:
						this.setState({
							sideRightIncentive: null,
						});
						break;
					default:
				}


				break;
			}
			case Flux.Constants.ConversionEventTypes.VIDEO_PREVIEW_ENDED: {
				const overlayFullSizeIncentive = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.OVERLAY_FULL_SIZE);

				this.setState({
					overlayFullSizeIncentive: overlayFullSizeIncentive,
				}, () => {
					this.toggleBodyScroll('overlayFullSizeIncentive', overlayFullSizeIncentive);
				});

				// solve more generic if more incentives than video player incentives are introduced
				if (overlayFullSizeIncentive && overlayFullSizeIncentive.type) {
					gaHelper('send', 'event', 'Conversion', overlayFullSizeIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: overlayFullSizeIncentive.type,
					});
				}

				break;
			}
			case Flux.Constants.ConversionEventTypes.PAGE_LOAD: {
				let overlayFullSizeIncentive;
				const sideLeftIncentive           = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.SIDE_LEFT);
				const sideRightIncentive          = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.SIDE_RIGHT);
				const exitIntentIncentive         = Flux.Conversion.getExitIntentIncentive();
				const exitIntentExtendedIncentive = Flux.Conversion.getExitIntentExtendedIncentive();
				const overlayIncentive            = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.OVERLAY);

				if (this.props.isMobile) {
					overlayFullSizeIncentive = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.OVERLAY_FULL_SIZE);
				}

				const newState = {
					overlayIncentive,
					sideLeftIncentive,
					sideRightIncentive,
					exitIntentIncentive,
					exitIntentExtendedIncentive,
				};

				if (this.props.isMobile) {
					newState.overlayFullSizeIncentive = overlayFullSizeIncentive;
					this.toggleBodyScroll('overlayFullSizeIncentive', overlayFullSizeIncentive);
				}

				this.setState(newState);

				if (sideLeftIncentive && sideLeftIncentive.type) {
					gaHelper('send', 'event', 'Conversion', sideLeftIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: sideLeftIncentive.type,
					});
				}
				if (sideRightIncentive && sideRightIncentive.type) {
					gaHelper('send', 'event', 'Conversion', sideRightIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: sideRightIncentive.type,
					});
				}
				// solve more generic if more incentives than video player incentives are introduced
				if (overlayFullSizeIncentive && overlayFullSizeIncentive.type) {
					gaHelper('send', 'event', 'Conversion', overlayFullSizeIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: overlayFullSizeIncentive.type,
					});
				}

				if (overlayIncentive && overlayIncentive.type) {
					gaHelper('send', 'event', 'Conversion', overlayIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: overlayIncentive.type,
					});
				}

				// tracking data
				Conversion.processTrackingData();

				break;
			}
			case Flux.Constants.ConversionEventTypes.BUY_IMAGE_ALBUM_OPENED: {
				const overlayFullSizeIncentive = Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.OVERLAY_FULL_SIZE);
				this.setState({
					overlayFullSizeIncentive: overlayFullSizeIncentive,
				}, () => {
					this.toggleBodyScroll('overlayFullSizeIncentive', overlayFullSizeIncentive);
				});
				// solve more generic if more incentives than video player incentives are introduced
				if (overlayFullSizeIncentive && overlayFullSizeIncentive.type) {
					gaHelper('send', 'event', 'Conversion', overlayFullSizeIncentive.type);
					gtagHelper(EnumGA4Events.DEFAULT_PARAMETER_EVENT, EnumGA4Events.CUSTOM_EVENT_CONVERSION, {
						[EnumGA4Events.CUSTOM_PARAMETER_CONVERSION]:    EnumGA4Events.CUSTOM_PARAMETER_CONVERSION__VALUE_OVERLAY,
						[EnumGA4Events.CUSTOM_PARAMETER_OVERLAY__TYPE]: overlayFullSizeIncentive.type,
					});
				}

				break;
			}
			// No default
		}
	}

	onMenuChange() {
		if (this.props.isMobile) {
			const menuOpen = Flux.VXMobile.getMenuOpen();

			if (menuOpen === Flux.Constants.VXMobile.Menus.NAVI || menuOpen === Flux.Constants.VXMobile.Menus.SEARCH) {
				this.setState({
					showSideLeftIncentive: false,
				});
			} else {
				this.setState({
					showSideLeftIncentive: true,
					sideLeftIncentive:     Flux.Conversion.getIncentiveByRegion(Flux.Constants.ConversionIncentiveRegions.SIDE_LEFT),
				});
			}
		}
	}

	toggleBodyScroll(type, incentive) {
		if (this.props.isMobile) {
			if (incentive) {
				Flux.VXMobile.disableBodyScroll(type);
			} else {
				Flux.VXMobile.enableBodyScroll(type);
			}
		}
	}

    onCountdownFinished() {
        this.setState({
            isCountdownFinished: true,
        });
    }

	render() {
		let exitIntentIncentive;
		let overlayFullSizeIncentive;
		let overlayIncentive;
		let sideLeftIncentive;
		let sideRightIncentive;
		let easterNotification;

		if (this.state.showEasterNotification) {
			easterNotification = withSuspense(<EggSuccessBox onCloseFn={this.onCloseEasterNotification} isMobile={this.props.isMobile} />);
		}
		if (this.state.overlayIncentive) {
			switch (this.state.overlayIncentive.type) {
				case Flux.Constants.ConversionIncentiveTypes.EVENT_EASTER_EGG_SEARCH:
					overlayIncentive = (
						withSuspense(<EasterEgg
							verificationToken={this.state.overlayIncentive.verificationToken}
							onCloseFn={this.onCloseOverlayIncentive}
						/>)
					);
					break;
				case Flux.Constants.ConversionIncentiveTypes.LUCY_CAT_VIDEO:
					overlayIncentive = (
                        withSuspense(
                            <LucyCatVideoBox incentive={this.state.overlayIncentive}
                                             isMobile={false}
                                             onCloseFn={this.onCloseOverlayIncentive}
                                             showConversionBox={false}
                            />)
					);
					break;
				default:
			}

		}

		if (this.state.overlayFullSizeIncentive) {
			let conversionBox;
			switch (this.state.overlayFullSizeIncentive.type) {
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_AVS:
					conversionBox = (
						withSuspense(<VideoPreviewEndedAvsBox videoPlayerIncentive={this.state.overlayFullSizeIncentive}
						                         isMobile={true}
						                         showCloseButton={true}
						                         onCloseFn={this.onCloseOverlayFullSizeIncentive}
						/>)
					);
					break;
				case    Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_SALE:
					conversionBox = (
						withSuspense(<VideoPreviewEndedSaleBox videoPlayerIncentive={this.state.overlayFullSizeIncentive}
						                          isMobile={true}
						                          showCloseButton={true}
						                          onCloseFn={this.onCloseOverlayFullSizeIncentive}
						/>)
					);
					break;
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_PAYMENT:
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_BUY:
					conversionBox = (
						withSuspense(<VideoPreviewEndedPurchaseBox videoPlayerIncentive={this.state.overlayFullSizeIncentive}
						                              isMobile={true}
						                              showCloseButton={true}
						                              onCloseFn={this.onCloseOverlayFullSizeIncentive}
						/>)
					);
					break;
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_LOGIN:
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_SIGNUP:
					conversionBox = (
						withSuspense(<VideoPreviewEndedSignupBox videoPlayerIncentive={this.state.overlayFullSizeIncentive}
						                            isMobile={true}
						                            showCloseButton={true}
						                            onCloseFn={this.onCloseOverlayFullSizeIncentive}
						/>)
					);
					break;
				case Flux.Constants.ConversionIncentiveTypes.VIDEO_PREVIEW_VIP:
					conversionBox = (
						withSuspense(<VideoPreviewEndedVipBox videoPlayerIncentive={this.state.overlayFullSizeIncentive}
						                         isMobile={true}
						                         showCloseButton={true}
						                         onCloseFn={this.onCloseOverlayFullSizeIncentive}
						/>)
					);
					break;
				case Flux.Constants.ConversionIncentiveTypes.IMAGE_ALBUM_BUY:
					conversionBox = (
						withSuspense(<BuyAlbumPurchaseBox imageAlbumIncentive={this.state.overlayFullSizeIncentive}
						                     isMobile={this.props.isMobile}
						                     showCloseButton={true}
						                     onCloseFn={this.onCloseOverlayFullSizeIncentive}
						                     className=" -width-buy-album"
						/>)
					);
					break;
				/*
			case Flux.Constants.ConversionIncentiveTypes.BALANCE_REFILL_COUPON:
				conversionBox = (<BalanceRefillPopup
						isMobile={true}
						bonusCode={this.state.overlayFullSizeIncentive.bonusCode}
						bonusAmount={this.state.overlayFullSizeIncentive.bonusAmount}
						incentiveType={this.state.overlayFullSizeIncentive.type}
						interval={this.state.overlayFullSizeIncentive.interval}
						layoutType={this.state.overlayFullSizeIncentive.layoutType}
						onCloseFn={this.onCloseOverlayFullSizeIncentive}
								 />);
				break;
*/
				default:
			}

			if (conversionBox) {
				overlayFullSizeIncentive = <div className="conversion-box__overlay -pos-fixed">{conversionBox}</div>;
			}
		}

		if (this.state.sideLeftIncentive && this.state.showSideLeftIncentive) {
			const incentive = this.state.sideLeftIncentive;
			switch (incentive.type) {
				case Flux.Constants.ConversionIncentiveTypes.SIDE_SIGNUP_FREE_VIDEO:
                    if (!this.state.isCountdownFinished) {
                        sideLeftIncentive = withSuspense(
                                <SideIncentive className="-side-incentive-free-clip"
                                               label={(Translations.get('SideIncentiveClipHeadline'))}
                                               isMobile={this.props.isMobile}
                                               isOpen={incentive.startOpen}
                                >
                                    <BonusCounterPopupClip incentive={incentive} onCountdownFinished={this.onCountdownFinished}/>
                                </SideIncentive>
                        );
                    }
					break;
				case Flux.Constants.ConversionIncentiveTypes.SALE_BONUS_COUNTER:
					sideLeftIncentive = withSuspense(
						<SideIncentive label={formatCurrency(incentive.bonusAmount) + ' ' + (Translations.get('SideIncentiveHeadline'))}
						               isMobile={this.props.isMobile}
						               isOpen={incentive.startOpen}
						>
							<BonusCounterPopup incentive={incentive} />
						</SideIncentive>
					);
					break;
				default:
			}
		}

		if (this.state.sideRightIncentive && this.state.showSideRightIncentive) {
			const incentive = this.state.sideRightIncentive;
			switch (incentive.type) {
				case Flux.Constants.ConversionIncentiveTypes.BALANCE_REFILL_COUPON:
					sideRightIncentive = withSuspense(
						<BalanceRefillPopup
							isMobile={this.props.isMobile}
							bonusCode={incentive.bonusCode}
							bonusAmount={incentive.bonusAmount}
							incentiveType={incentive.type}
							interval={incentive.interval}
						/>
					);
					break;
				default:
			}
		}

		if (this.state.exitIntentIncentive && this.state.showExitIntentIncentive) {
			const incentive = this.state.exitIntentIncentive;
			switch (incentive.type) {
				case Flux.Constants.ConversionIncentiveTypes.EXIT_INTENT_DEFAULT:
					exitIntentIncentive = withSuspense(<ExitIntentBox onCloseFn={this.onCloseExitIncentive} />);
					break;
				default:
			}
		}

		if (this.state.exitIntentExtendedIncentive) {
			openModalBox(ModalBoxEnum.EXIT_INTENT, {overCookie: true, incentive: this.state.exitIntentExtendedIncentive});
		}

		return (
			<div>
				{exitIntentIncentive}
				{overlayIncentive}
				{overlayFullSizeIncentive}
				{sideLeftIncentive}
				{sideRightIncentive}
				{easterNotification}
			</div>
		);
	}
}

Conversion.defaultProps = {
	isMobile: false,
};

Conversion.propTypes = {
	isMobile: PropTypes.bool,
};

export default Conversion;
